<template>
  <InfusePageLayout>
    <div class="flex flex-col gap-6 overflow-y-auto">
      <RolesTable
        :rows="roles"
        :active="(row: Role, _: number) => route?.params?.id?.toString() === row.id"
        :loading="isLoading || isDeletingRole"
        :page="currentPage"
        :has-more-items="hasNextPage"
        :search-enabled="true"
        :search-query="searchQuery"
        :removable="true"
        :editable="true"
        :level="userLevel"
        @click-row="redirectToRole"
        @load="fetchNextPage"
        @search="search($event)"
        @create="showCreateRoleModal = true"
        @remove="
          (row: Role) => {
            activeRole = row;
            showDeleteRoleModal = true;
          }
        "
        @edit="
          (row: Role) => {
            activeRole = row;
            showUpdateRoleModal = true;
          }
        "
        @promote="onPromoteRole"
        @demote="onDemoteRole"
        @set-ranking="onSetRanking"
        :loading-ranking="isDemotingRole || isPromotingRole || isSettingRanking"
        :role-creation-permission="maxUserCreateRolePermission"
      />
    </div>
  </InfusePageLayout>

  <!-- CREATE -->
  <MutationRoleModal
    v-if="showCreateRoleModal"
    @close="closeCreateModal"
    @mutate="onCreateRole"
    :loading="isCreatingRole"
  />

  <!-- UPDATE -->
  <MutationRoleModal
    v-if="showUpdateRoleModal && activeRole"
    :role="activeRole"
    :loading="isUpdatingRole"
    @close="closeUpdateModal"
    @mutate="onUpdateRole"
  />

  <InfuseConfirmModal
    v-if="showDeleteRoleModal"
    :action-title="t('iam.roles.header.delete_role')"
    :action-description="t('iam.roles.info.delete_role', { role: activeRole?.name || '' })"
    verification-text="delete"
    @confirm="onDeleteRole"
    @close="closeDeleteModal"
  />
</template>

<script setup lang="ts">
import { useRoles } from '@infuse/iam/role/queries';
import { InfusePageLayout } from '@infuse/ui/layouts';
import { useRoute, useRouter } from 'vue-router';
import { RolesTable, MutationRoleModal, getRoleUrlParameters } from '@infuse/iam/role/ui';
import { computed, ref, watch } from 'vue';
import {
  useCreateRole,
  useDeleteRole,
  useUpdateRole,
  usePromoteRole,
  useDemoteRole,
  useSetRanking
} from '@infuse/iam/role/mutations';
import { CreateRole, Role, UpdateRole } from '@infuse/iam/role/type';
import { InfuseConfirmModal } from '@infuse/ui/components';
import { useI18n } from 'vue-i18n';
import { useCurrentUserStore } from '@infuse/iam/user/store';
import { rolePermissions } from '@infuse/api/iam/role';
import { tenantRolePermissions } from '@infuse/api/iam/tenant';
import { organisationRolePermissions } from '@infuse/api/iam/organisation';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const activeRole = ref<Role | undefined>(undefined);

const showCreateRoleModal = ref(false);
const showUpdateRoleModal = ref(false);
const showDeleteRoleModal = ref(false);

const { roles, isLoading, hasNextPage, searchQuery, currentPage, fetchNextPage } = useRoles();
const { mutateAsync: createRole, isPending: isCreatingRole } = useCreateRole();
const { mutateAsync: updateRole, isPending: isUpdatingRole } = useUpdateRole();
const { mutateAsync: deleteRole, isPending: isDeletingRole } = useDeleteRole();
const { mutateAsync: promoteRole, isPending: isPromotingRole } = usePromoteRole();
const { mutateAsync: demoteRole, isPending: isDemotingRole } = useDemoteRole();
const { mutateAsync: setRanking, isPending: isSettingRanking } = useSetRanking();

const currentUserStore = useCurrentUserStore();

watch(
  () => route.query.search,
  (search) => {
    searchQuery.value = search?.toString();
  },
  { immediate: true }
);

const maxUserCreateRolePermission = computed(() => {
  switch (userLevel.value) {
    case 'organisation':
      return organisationRolePermissions.CREATE;
    case 'tenant':
      return tenantRolePermissions.CREATE;
    default:
      return rolePermissions.CREATE;
  }
});

async function search(search: string): Promise<void> {
  searchQuery.value = search;
  replaceQueryParams(searchQuery.value);
}

function replaceQueryParams(searchQuery: string | undefined) {
  if (router.currentRoute.value.name !== 'role') {
    // only do this if the user is currently on this page and not some sub-route.
    return;
  }

  router.replace({
    name: 'role',
    query: {
      search: searchQuery || undefined
    }
  });
}
const userLevel = computed(() => currentUserStore.level || 'domain');

function redirectToRole(row: Role): void {
  let routeName = 'role.permissions';
  const rolePathParams = getRoleUrlParameters(row);

  if (router.currentRoute.value.name !== 'role') {
    routeName = router.currentRoute.value.name?.toString() || routeName;
  }

  router.push({
    name: routeName,
    params: { id: row.id, level: rolePathParams?.level, levelId: rolePathParams?.levelId }
  });
}

async function onCreateRole(vars: { role: CreateRole }): Promise<void> {
  await createRole({ role: vars.role });
  closeCreateModal();
}

function closeCreateModal() {
  showCreateRoleModal.value = false;
}

async function onDeleteRole(): Promise<void> {
  if (!activeRole.value) {
    return;
  }

  await deleteRole({ roleId: activeRole.value.id });
  closeDeleteModal();
}

function closeDeleteModal() {
  showDeleteRoleModal.value = false;
  activeRole.value = undefined;
}

async function onUpdateRole(vars: { id: string; role: UpdateRole }): Promise<void> {
  await updateRole({ roleId: vars.id, role: vars.role });
  closeUpdateModal();
}

async function onPromoteRole(role: Role): Promise<void> {
  await promoteRole({ roleId: role.id });
}

async function onDemoteRole(role: Role): Promise<void> {
  await demoteRole({ roleId: role.id });
}

async function onSetRanking(role: Role, ranking: number): Promise<void> {
  await setRanking({ roleId: role.id, ranking });
}

function closeUpdateModal() {
  showUpdateRoleModal.value = false;
  activeRole.value = undefined;
}
</script>
